import React, { useEffect, useState } from 'react';

import { Theme } from '../types/theme';
import { Spinner } from '../components/spinner';
import * as serviceWorker from '../serviceWorker';
import { GameDocument } from '../types/game-document';
import { useGameTheme } from '../hooks/use-game-theme';
import { useGameDocument } from '../hooks/use-game-document';
import { putToStore, getFromStore } from '../utils/indexedDB';
import { AssessmentEntity } from '../types/state/assessment/assessment';

const GameDocumentContext = React.createContext<
  [
    initialState: GameDocumentState,
    setState: React.Dispatch<React.SetStateAction<GameDocumentState>>
  ]
>([{}, () => {}]);

interface GameDocumentProviderProps {
  gameCode: string;
  isFromFacilitator?: boolean;
  children: React.ReactNode;
}

const GameDocumentProvider = ({
  gameCode,
  isFromFacilitator,
  ...props
}: GameDocumentProviderProps) => {
  const gameDocument = useGameDocument(gameCode, isFromFacilitator);
  const gameTheme = useGameTheme(gameCode, isFromFacilitator);
  const [state, setState] = useState<GameDocumentState>({
    gameCode
  });

  const loadServiceWorker = async () => {
    // ask notification permission
    await serviceWorker.askPermission();
    await new Promise((r) => {
      serviceWorker.register({ onSuccess: (e) => console.log(e) });
    });
  };

  useEffect(() => {
    loadServiceWorker();
    const loadGameDocument = async () => {
      const game = await getFromStore<GameDocumentState>(
        'game-document',
        gameCode
      );
      if (game && game.gameDocument) {
        setState(game);
      } else {
        const content: GameDocumentState = {
          gameCode,
          gameDocument: gameDocument.data,
          theme: gameTheme.data
        };
        setState(content);
        putToStore('game-document', content);
      }
    };

    loadGameDocument();
  }, [gameCode, gameDocument.data, gameTheme.data]);

  return (
    <GameDocumentContext.Provider value={[state, setState]}>
      {state.gameDocument ? props.children : <Spinner />}
    </GameDocumentContext.Provider>
  );
};

export interface GameDocumentState {
  gameCode?: string;
  gameDocument?: GameDocument;
  assessmentDocument?: AssessmentEntity;
  theme?: Theme;
}

export { GameDocumentContext, GameDocumentProvider };
